import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, Loader, MessageCircle, Bot, Hand, ExternalLink, Link, Copy, Check, ChevronDown, ClipboardList, Plus, Trash2, GripVertical, X, Clock, Settings, Smartphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useBookingSystem } from '@/contexts/BookingSystemContext'
import { useArtistsData } from '@/contexts/ArtistsDataContext'
import { AppSettings } from '@/components/shared/AppSettings'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { doc, updateDoc, onSnapshot } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { isNative } from '@/lib/platform'
import { Browser } from '@capacitor/browser'
import { motion } from 'framer-motion'
import { iosIsNative } from '@/lib/iosCalendar'

export default function BookingSystemSettingsPage() {
  const navigate = useNavigate()
  const { googleCalendar, iosCalendar, connectGoogleCalendar, disconnectGoogleCalendar, connectIosCalendar, disconnectIosCalendar, welcomeMessage, updateWelcomeMessage, chatSettings, artistId } = useBookingSystem()
  const { allArtists } = useArtistsData()
  const [isConnecting, setIsConnecting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [isConnectingIos, setIsConnectingIos] = useState(false)
  const [isDisconnectingIos, setIsDisconnectingIos] = useState(false)
  const [isEditingWelcomeMessage, setIsEditingWelcomeMessage] = useState(false)
  const [isSavingWelcomeMessage, setIsSavingWelcomeMessage] = useState(false)
  const [editedMessage, setEditedMessage] = useState(welcomeMessage?.content || '')
  const [isSavingChatMode, setIsSavingChatMode] = useState(false)
  const [selectedMode, setSelectedMode] = useState<'ai_assistant' | 'welcome_message' | 'off'>(chatSettings?.mode || 'off')
  const [copiedBookingLink, setCopiedBookingLink] = useState(false)
  const [isClientBookingInfoExpanded, setIsClientBookingInfoExpanded] = useState(false)
  const [isAddingQuestion, setIsAddingQuestion] = useState(false)
  const [isSavingQuestion, setIsSavingQuestion] = useState(false)
  const [newFieldType, setNewFieldType] = useState<'input' | 'consentForm'>('input')
  const [newFieldData, setNewFieldData] = useState({
    displayText: '',
    hint: '',
    isRequired: true,
    content: '' // for consent forms
  })
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [viewingConsentIndex, setViewingConsentIndex] = useState<number | null>(null)
  const [isAppSettingsExpanded, setIsAppSettingsExpanded] = useState(false)
  const [isGoogleCalendarExpanded, setIsGoogleCalendarExpanded] = useState(false)
  const [isBookingLinkExpanded, setIsBookingLinkExpanded] = useState(false)
  const [isChatModeExpanded, setIsChatModeExpanded] = useState(false)
  const [isReminderExpanded, setIsReminderExpanded] = useState(false)
  
  // Refs for accordion sections
  const appSettingsRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const bookingLinkRef = useRef<HTMLDivElement>(null)
  const chatModeRef = useRef<HTMLDivElement>(null)
  const reminderRef = useRef<HTMLDivElement>(null)
  const clientBookingInfoRef = useRef<HTMLDivElement>(null)
  const [reminderEnabled, setReminderEnabled] = useState<boolean>(false)
  const [reminderTimeValue, setReminderTimeValue] = useState<number>(1)
  const [reminderTimeUnit, setReminderTimeUnit] = useState<'hours' | 'days'>('days')
  const [updatingReminder, setUpdatingReminder] = useState<boolean>(false)
  const [reminderCustomMessage, setReminderCustomMessage] = useState<string>('')
  const [editedReminderMessage, setEditedReminderMessage] = useState('')
  const [isSavingReminderMessage, setIsSavingReminderMessage] = useState(false)
  const [customMessageEnabled, setCustomMessageEnabled] = useState<boolean>(false)
  const { requiredInformation } = useBookingSystem()
  
  // Get current artist data
  const currentArtist = artistId ? allArtists.find(a => a.id === artistId) : null
  const bookingLink = currentArtist?.displayName ? `getmoretats.com/${currentArtist.displayName}` : ''

  // Sync selectedMode with chatSettings changes
  useEffect(() => {
    if (chatSettings?.mode) {
      setSelectedMode(chatSettings.mode)
      // Exit editing mode when switching away from welcome message
      if (chatSettings.mode !== 'welcome_message') {
        setIsEditingWelcomeMessage(false)
      }
    }
  }, [chatSettings?.mode])

  // Subscribe to booking reminder settings
  useEffect(() => {
    if (!artistId) return
    const bookingSystemRef = doc(db, 'bookingSystems', artistId)
    const unsub = onSnapshot(bookingSystemRef, (snapshot) => {
      const data = snapshot.data()
      const reminder = data?.bookingReminderNotification
      if (reminder) {
        setReminderEnabled(!!reminder.enabled)
        const timeBefore = reminder.timeBefore || 24
        // Convert from hours: if >= 24 and divisible by 24, show as days
        if (timeBefore >= 24 && timeBefore % 24 === 0) {
          setReminderTimeValue(timeBefore / 24)
          setReminderTimeUnit('days')
        } else {
          setReminderTimeValue(timeBefore)
          setReminderTimeUnit('hours')
        }
        // Load custom message
        setReminderCustomMessage(reminder.customMessage || '')
        setEditedReminderMessage(reminder.customMessage || '')
        // Load custom message enabled state
        setCustomMessageEnabled(!!reminder.customMessageEnabled)
      }
    })
    return () => unsub()
  }, [artistId])

  const handleToggleReminder = async () => {
    if (!artistId) return
    const nextEnabled = !reminderEnabled
    try {
      setUpdatingReminder(true)
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      const timeBeforeInHours = reminderTimeUnit === 'days' ? reminderTimeValue * 24 : reminderTimeValue
      await updateDoc(bookingSystemRef, {
        'bookingReminderNotification.enabled': nextEnabled,
        'bookingReminderNotification.timeBefore': timeBeforeInHours
      })
    } catch (error) {
      console.error('[BookingSystemSettings] Error toggling reminder:', error)
      toast.error('Failed to update reminder settings')
    } finally {
      setUpdatingReminder(false)
    }
  }

  const handleSaveReminderTime = async (value?: number, unit?: 'hours' | 'days') => {
    if (!artistId) return
    const finalValue = value ?? reminderTimeValue
    const finalUnit = unit ?? reminderTimeUnit
    try {
      setUpdatingReminder(true)
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      const timeBeforeInHours = finalUnit === 'days' ? finalValue * 24 : finalValue
      await updateDoc(bookingSystemRef, {
        'bookingReminderNotification.timeBefore': timeBeforeInHours
      })
    } catch (error) {
      console.error('[BookingSystemSettings] Error saving reminder time:', error)
      toast.error('Failed to save reminder time')
    } finally {
      setUpdatingReminder(false)
    }
  }

  const handleSaveReminderMessage = async () => {
    if (!artistId) return
    try {
      setIsSavingReminderMessage(true)
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      await updateDoc(bookingSystemRef, {
        'bookingReminderNotification.customMessage': editedReminderMessage
      })
      setReminderCustomMessage(editedReminderMessage)
      toast.success('Reminder message saved successfully!')
    } catch (error) {
      console.error('[BookingSystemSettings] Error saving reminder message:', error)
      toast.error('Failed to save reminder message')
    } finally {
      setIsSavingReminderMessage(false)
    }
  }

  const handleCancelReminderMessageEdit = () => {
    setEditedReminderMessage(reminderCustomMessage)
  }

  const handleToggleCustomMessage = async () => {
    if (!artistId) return
    const nextEnabled = !customMessageEnabled
    try {
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      await updateDoc(bookingSystemRef, {
        'bookingReminderNotification.customMessageEnabled': nextEnabled
      })
      setCustomMessageEnabled(nextEnabled)
    } catch (error) {
      console.error('[BookingSystemSettings] Error toggling custom message:', error)
      toast.error('Failed to update custom message settings')
    }
  }

  // Helper function to scroll section into view
  const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }

  const handleConnectGoogleCalendar = async () => {
    if (isNative()) {
      // On native app, open browser to settings page using Capacitor Browser plugin
      try {
        await Browser.open({ url: 'https://getmoretats.com/booking-system/settings' })
      } catch (error) {
        console.error('Failed to open browser:', error)
        toast.error('Failed to open browser')
      }
      return
    }

    // On web, use the OAuth popup flow
    try {
      setIsConnecting(true)
      await connectGoogleCalendar()
      toast.success('Google Calendar connected successfully!')
    } catch (error) {
      toast.error('Failed to connect Google Calendar')
    } finally {
      setIsConnecting(false)
    }
  }

  const handleDisconnectGoogleCalendar = async () => {
    try {
      setIsDisconnecting(true)
      await disconnectGoogleCalendar()
      toast.success('Google Calendar disconnected')
    } catch (error) {
      toast.error('Failed to disconnect Google Calendar')
    } finally {
      setIsDisconnecting(false)
    }
  }

  const handleConnectIosCalendar = async () => {
    if (!iosIsNative()) {
      toast.error('iPhone calendar is only available on iOS app')
      return
    }

    try {
      setIsConnectingIos(true)
      await connectIosCalendar()
      toast.success('iPhone calendar connected successfully!')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to connect iPhone calendar'
      console.error('[BookingSystemSettings] Error connecting iPhone calendar:', message)
      toast.error(message)
    } finally {
      setIsConnectingIos(false)
    }
  }

  const handleDisconnectIosCalendar = async () => {
    try {
      setIsDisconnectingIos(true)
      await disconnectIosCalendar()
      toast.success('iPhone calendar disconnected')
    } catch (error) {
      toast.error('Failed to disconnect iPhone calendar')
    } finally {
      setIsDisconnectingIos(false)
    }
  }

  const handleSaveWelcomeMessage = async () => {
    try {
      setIsSavingWelcomeMessage(true)
      await updateWelcomeMessage({
        enabled: welcomeMessage?.enabled || false,
        content: editedMessage
      })
      setIsEditingWelcomeMessage(false)
      toast.success('Welcome message saved successfully!')
    } catch (error) {
      toast.error('Failed to save welcome message')
    } finally {
      setIsSavingWelcomeMessage(false)
    }
  }

  const handleCancelEdit = () => {
    setEditedMessage(welcomeMessage?.content || '')
    setIsEditingWelcomeMessage(false)
  }

  const handleChangeChatMode = async (newMode: 'ai_assistant' | 'welcome_message' | 'off') => {
    if (!artistId) return
    
    try {
      setIsSavingChatMode(true)
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      await updateDoc(bookingSystemRef, {
        'chatSettings.mode': newMode,
        'chatSettings.updatedAt': new Date()
      })
      setSelectedMode(newMode)
      
      // Exit editing mode when switching away from welcome message
      if (newMode !== 'welcome_message') {
        setIsEditingWelcomeMessage(false)
      }
      
      const modeLabel = newMode === 'ai_assistant' ? 'AI Assistant' : newMode === 'welcome_message' ? 'Welcome Message' : 'Off'
      toast.success(`Chat mode changed to ${modeLabel}`)
    } catch (error) {
      console.error('[BookingSystemSettings] Error changing chat mode:', error)
      toast.error('Failed to change chat mode')
    } finally {
      setIsSavingChatMode(false)
    }
  }

  const handleCopyBookingLink = async () => {
    try {
      await navigator.clipboard.writeText(`https://${bookingLink}`)
      setCopiedBookingLink(true)
      toast.success('Link copied to clipboard!')
      setTimeout(() => setCopiedBookingLink(false), 2000)
    } catch (error) {
      toast.error('Failed to copy link')
    }
  }

  const handleAddQuestion = async () => {
    if (!artistId) return
    
    // Validation depends on field type
    if (newFieldType === 'input' && !newFieldData.displayText.trim()) {
      toast.error('Please enter a question or field name')
      return
    }
    if (newFieldType === 'consentForm' && !newFieldData.content.trim()) {
      toast.error('Please enter the consent form content')
      return
    }

    try {
      setIsSavingQuestion(true)
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      
      // Build the new field config
      const newField: any = {
        type: newFieldType,
        displayText: newFieldType === 'consentForm' ? 'Consent Form' : newFieldData.displayText,
        isRequired: newFieldData.isRequired
      }
      
      // Only add hint for input fields
      if (newFieldType === 'input' && newFieldData.hint) {
        newField.hint = newFieldData.hint
      }
      
      // Add content for consent forms
      if (newFieldType === 'consentForm') {
        newField.content = newFieldData.content
      }

      // Update Firestore with the new field - using bookingClientInfo array format
      const updatedRequiredInfo = Array.isArray(requiredInformation) 
        ? [...requiredInformation, newField]
        : [newField]

      await updateDoc(bookingSystemRef, {
        'bookingClientInfo': updatedRequiredInfo
      })

      toast.success('Question added successfully!')
      setIsAddingQuestion(false)
      setNewFieldData({
        displayText: '',
        hint: '',
        isRequired: true,
        content: ''
      })
      setNewFieldType('input')
    } catch (error) {
      console.error('Error adding question:', error)
      toast.error('Failed to add question')
    } finally {
      setIsSavingQuestion(false)
    }
  }

  const handleDeleteQuestion = async (indexOrFieldName: string | number) => {
    if (!artistId) return

    try {
      setIsSavingQuestion(true)
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      
      const updateData: any = {}
      
      // Handle array format (bookingClientInfo)
      if (Array.isArray(requiredInformation)) {
        const index = typeof indexOrFieldName === 'number' ? indexOrFieldName : parseInt(indexOrFieldName)
        const updatedRequiredInfo = requiredInformation.filter((_, i) => i !== index)
        updateData['bookingClientInfo'] = updatedRequiredInfo
      } else {
        // Handle map format (bookingRequiredInformation - legacy)
        const updatedRequiredInfo = { ...(requiredInformation || {}) }
        delete updatedRequiredInfo[indexOrFieldName]
        updateData['bookingRequiredInformation'] = updatedRequiredInfo
      }

      await updateDoc(bookingSystemRef, updateData)

      toast.success('Question deleted successfully!')
    } catch (error) {
      console.error('Error deleting question:', error)
      toast.error('Failed to delete question')
    } finally {
      setIsSavingQuestion(false)
    }
  }

  const handleReorderQuestions = async (fromIndex: number, toIndex: number) => {
    if (!artistId || !Array.isArray(requiredInformation)) return
    if (fromIndex === toIndex) return
    
    try {
      const newOrder = [...requiredInformation]
      const [movedItem] = newOrder.splice(fromIndex, 1)
      newOrder.splice(toIndex, 0, movedItem)
      
      const bookingSystemRef = doc(db, 'bookingSystems', artistId)
      await updateDoc(bookingSystemRef, {
        'bookingClientInfo': newOrder
      })
      
      toast.success('Question order updated!')
      setDraggedIndex(null)
    } catch (error) {
      console.error('Error reordering questions:', error)
      toast.error('Failed to reorder questions')
      setDraggedIndex(null)
    }
  }

  return (
    <div className="min-h-screen bg-background" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
      {/* Header */}
      <div className="phone-container-padded pt-4 pb-0">
        <div className="mb-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="h-10 w-10 rounded-lg transition-all hover:shadow-lg bg-background dark:bg-card border border-border flex items-center justify-center"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5 text-foreground" />
          </button>
          <h1 className="text-page-title text-foreground">
            Settings
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="phone-container-padded pb-4">
        <div className="space-y-2">
          {/* App Settings Section */}
          <div className="bg-card rounded-lg overflow-hidden" ref={appSettingsRef}>
            {/* Dropdown Header */}
            <button
              onClick={() => {
                setIsAppSettingsExpanded(!isAppSettingsExpanded)
                if (!isAppSettingsExpanded) scrollToSection(appSettingsRef)
              }}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <Settings className="w-5 h-5 flex-shrink-0" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold">App Settings</h2>
                  <p className="text-xs text-muted-foreground">Theme, language & notifications</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ${
                  isAppSettingsExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown Content */}
            <div
              className={`overflow-hidden transition-all duration-200 ${
                isAppSettingsExpanded ? 'max-h-[800px]' : 'max-h-0'
              }`}
            >
              <div className="border-t border-input p-4">
                <AppSettings showLogout={false} />
              </div>
            </div>
          </div>

          {/* Calendar Section - iPhone or Google Calendar */}
          <div className="bg-card rounded-lg overflow-hidden" ref={calendarRef}>
            {/* Dropdown Header */}
            <button
              onClick={() => {
                setIsGoogleCalendarExpanded(!isGoogleCalendarExpanded)
                if (!isGoogleCalendarExpanded) scrollToSection(calendarRef)
              }}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <Calendar className="w-5 h-5 flex-shrink-0" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold">Calendar</h2>
                  <p className="text-xs text-muted-foreground">Connect Google or iPhone calendar</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ${
                  isGoogleCalendarExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown Content */}
            <div
              className={`overflow-hidden transition-all duration-200 ${
                isGoogleCalendarExpanded ? 'max-h-[800px]' : 'max-h-0'
              }`}
            >
              <div className="border-t border-input p-4 space-y-4">
                {/* iPhone Calendar Option */}
                {iosIsNative() && (
                  <div className="space-y-3">
                    <div className="border-b border-input pb-4">
                      <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <Smartphone className="w-4 h-4" />
                        iPhone Calendar
                      </h3>

                      {iosCalendar.isConnected ? (
                        <div className="space-y-3">
                          <div className="bg-muted/50 rounded p-3">
                            <p className="text-sm font-medium mb-1">Status</p>
                            <p className="text-sm text-green-600">Connected</p>
                            {iosCalendar.lastSynced && !isNaN(iosCalendar.lastSynced.getTime()) && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Last synced: {format(iosCalendar.lastSynced, 'MMM d, yyyy h:mm a')}
                              </p>
                            )}
                          </div>

                          <Button
                            onClick={handleDisconnectIosCalendar}
                            disabled={isDisconnectingIos}
                            variant="outline"
                            className="w-full"
                          >
                            {isDisconnectingIos ? (
                              <>
                                <Loader className="w-4 h-4 mr-2 animate-spin" />
                                Disconnecting...
                              </>
                            ) : (
                              'Disconnect iPhone Calendar'
                            )}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={handleConnectIosCalendar}
                          disabled={isConnectingIos}
                          className="w-full"
                        >
                          {isConnectingIos ? (
                            <>
                              <Loader className="w-4 h-4 mr-2 animate-spin" />
                              Connecting...
                            </>
                          ) : (
                            'Connect iPhone Calendar'
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Google Calendar Option */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Google Calendar</h3>

                  {googleCalendar.isConnected ? (
                    <div className="space-y-3">
                      {/* Account Info */}
                      <div className="bg-muted/50 rounded p-3">
                        <p className="text-sm font-medium mb-1">Connected Account</p>
                        <p className="text-sm text-muted-foreground">{googleCalendar.email}</p>
                        {googleCalendar.lastSynced && !isNaN(googleCalendar.lastSynced.getTime()) && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Last synced: {format(googleCalendar.lastSynced, 'MMM d, yyyy h:mm a')}
                          </p>
                        )}
                      </div>

                      {/* Disconnect Button */}
                      <Button
                        onClick={handleDisconnectGoogleCalendar}
                        disabled={isDisconnecting}
                        variant="outline"
                        className="w-full"
                      >
                        {isDisconnecting ? (
                          <>
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                            Disconnecting...
                          </>
                        ) : (
                          'Disconnect Google Calendar'
                        )}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={handleConnectGoogleCalendar}
                      disabled={isConnecting}
                      className="w-full"
                    >
                      {isConnecting ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : isNative() ? (
                        <>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Connect in Browser
                        </>
                      ) : (
                        'Connect Google Calendar'
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* My Booking Link Section */}
          <div className="bg-card rounded-lg overflow-hidden" ref={bookingLinkRef}>
            {/* Dropdown Header */}
            <button
              onClick={() => {
                setIsBookingLinkExpanded(!isBookingLinkExpanded)
                if (!isBookingLinkExpanded) scrollToSection(bookingLinkRef)
              }}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <Link className="w-5 h-5 flex-shrink-0" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold">My Booking Link</h2>
                  <p className="text-xs text-muted-foreground">Share your direct booking URL</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ${
                  isBookingLinkExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown Content */}
            <div
              className={`overflow-hidden transition-all duration-200 ${
                isBookingLinkExpanded ? 'max-h-[500px]' : 'max-h-0'
              }`}
            >
              <div className="border-t border-input p-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Direct link to your booking chat
                </p>

                {bookingLink ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm font-mono text-foreground break-all">
                      {bookingLink}
                    </code>
                    <button
                      onClick={handleCopyBookingLink}
                      className="flex-shrink-0 p-2 hover:bg-muted rounded transition-colors"
                      aria-label="Copy link"
                      title="Copy link"
                    >
                      {copiedBookingLink ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <Copy className="w-5 h-5 text-muted-foreground" />
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                    Loading booking link...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Chat Mode Selection */}
          <div className="bg-card rounded-lg overflow-hidden" ref={chatModeRef}>
            {/* Dropdown Header */}
            <button
              onClick={() => {
                setIsChatModeExpanded(!isChatModeExpanded)
                if (!isChatModeExpanded) scrollToSection(chatModeRef)
              }}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <Hand className="w-5 h-5 flex-shrink-0" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold">Chat Mode</h2>
                  <p className="text-xs text-muted-foreground">AI, welcome message, or off</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ${
                  isChatModeExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown Content */}
            <div
              className={`overflow-hidden transition-all duration-200 ${
                isChatModeExpanded ? 'max-h-[1200px]' : 'max-h-0'
              }`}
            >
              <div className="border-t border-input p-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Choose how you want to handle incoming messages. You can only use one at a time.
                </p>
                
                <div className="space-y-3">
                  {/* AI Assistant Option */}
                  <button
                    onClick={() => handleChangeChatMode('ai_assistant')}
                    disabled={isSavingChatMode}
                    className={`w-full p-4 rounded-lg border-2 transition-all ${
                      selectedMode === 'ai_assistant'
                        ? 'border-primary bg-primary/5'
                        : 'border-input bg-background hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 pt-1">
                        <Bot className={`w-5 h-5 ${selectedMode === 'ai_assistant' ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      <div className="text-left flex-1">
                        <p className={`font-semibold ${selectedMode === 'ai_assistant' ? 'text-foreground' : 'text-muted-foreground'}`}>
                          AI Assistant
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Automatically respond to initial messages with AI
                        </p>
                      </div>
                      {selectedMode === 'ai_assistant' && (
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>

                  {/* Welcome Message Option */}
                  <button
                    onClick={() => handleChangeChatMode('welcome_message')}
                    disabled={isSavingChatMode}
                    className={`w-full p-4 rounded-lg border-2 transition-all ${
                      selectedMode === 'welcome_message'
                        ? 'border-primary bg-primary/5'
                        : 'border-input bg-background hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 pt-1">
                        <MessageCircle className={`w-5 h-5 ${selectedMode === 'welcome_message' ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      <div className="text-left flex-1">
                        <p className={`font-semibold ${selectedMode === 'welcome_message' ? 'text-foreground' : 'text-muted-foreground'}`}>
                          Welcome Message
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Show a custom message when users first open the chat
                        </p>
                      </div>
                      {selectedMode === 'welcome_message' && (
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>

                  {/* Off Option */}
                  <button
                    onClick={() => handleChangeChatMode('off')}
                    disabled={isSavingChatMode}
                    className={`w-full p-4 rounded-lg border-2 transition-all ${
                      selectedMode === 'off'
                        ? 'border-primary bg-primary/5'
                        : 'border-input bg-background hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 pt-1">
                        <div className={`w-5 h-5 ${selectedMode === 'off' ? 'text-primary' : 'text-muted-foreground'}`}>
                          <svg fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <div className="text-left flex-1">
                        <p className={`font-semibold ${selectedMode === 'off' ? 'text-foreground' : 'text-muted-foreground'}`}>
                          Off
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          No automatic response or welcome message
                        </p>
                      </div>
                      {selectedMode === 'off' && (
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                </div>

                {/* Welcome Message Settings - Only show when Welcome Message mode is selected */}
                {selectedMode === 'welcome_message' && (
                  <div className="border-t border-input pt-4 mt-4 space-y-4">
                    <h3 className="font-semibold text-sm">Welcome Message Settings</h3>
                    
                    <p className="text-sm text-muted-foreground">
                      Set a custom message that appears when users first enter your chat.
                    </p>

                    {/* Message Preview */}
                    {!isEditingWelcomeMessage && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">Preview</p>
                        <div className="bg-background rounded-lg p-4 min-h-[120px]">
                          {/* Chat preview - matches ChatMessage styling */}
                          <div className="flex items-end gap-2">
                            {/* Avatar */}
                            <div className="w-8 h-8 flex-shrink-0 rounded-full bg-primary flex items-center justify-center overflow-hidden">
                              {currentArtist?.avatarUrl ? (
                                <img
                                  src={currentArtist.avatarUrl}
                                  alt={currentArtist.displayName}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-primary-foreground text-xs font-semibold">
                                  {(currentArtist?.displayName || 'A').charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            {/* Message bubble */}
                            <div className="flex flex-col items-start">
                              <div className="bg-gray-100 text-black rounded-[18px] rounded-bl-none px-4 py-3 max-w-xs">
                                <p className="text-sm break-words whitespace-pre-wrap leading-[1.5]">
                                  {welcomeMessage?.content || '(No message set)'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Edit Mode */}
                    {isEditingWelcomeMessage && (
                      <div className="space-y-3">
                        <textarea
                          value={editedMessage}
                          onChange={(e) => setEditedMessage(e.target.value)}
                          placeholder="Enter your welcome message..."
                          className="w-full min-h-[120px] p-3 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-0"
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={handleSaveWelcomeMessage}
                            disabled={isSavingWelcomeMessage}
                            className="flex-1"
                          >
                            {isSavingWelcomeMessage ? (
                              <>
                                <Loader className="w-4 h-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              'Save'
                            )}
                          </Button>
                          <Button
                            onClick={handleCancelEdit}
                            disabled={isSavingWelcomeMessage}
                            variant="outline"
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Edit Button */}
                    {!isEditingWelcomeMessage && (
                      <Button
                        onClick={() => setIsEditingWelcomeMessage(true)}
                        variant="outline"
                        className="w-full"
                      >
                        Edit Message
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Booking Reminder Section */}
          <div className="bg-card rounded-lg overflow-hidden" ref={reminderRef}>
            {/* Dropdown Header */}
            <button
              onClick={() => {
                setIsReminderExpanded(!isReminderExpanded)
                if (!isReminderExpanded) scrollToSection(reminderRef)
              }}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <Clock className="w-5 h-5 flex-shrink-0" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold">Booking Reminder</h2>
                  <p className="text-xs text-muted-foreground">Automatic reminders to clients</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ${
                  isReminderExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown Content */}
            <div
              className={`overflow-hidden transition-all duration-200 ${
                isReminderExpanded ? 'max-h-[600px]' : 'max-h-0'
              }`}
            >
              <div className="border-t border-input p-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Automatically send a reminder message to clients before their booking.
                </p>

                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Enable Reminders</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {reminderEnabled ? 'Reminders are enabled' : 'Reminders are disabled'}
                    </p>
                  </div>
                  <button
                    onClick={handleToggleReminder}
                    disabled={updatingReminder}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                      reminderEnabled ? 'bg-primary' : 'bg-gray-300'
                    }`}
                    aria-pressed={reminderEnabled}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        reminderEnabled ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Time Before Setting */}
                {reminderEnabled && (
                  <div className="space-y-4 pt-2 border-t border-input">
                    <div className="space-y-3">
                      <p className="font-medium text-sm">Send Reminder</p>
                      <div className="flex items-center gap-2">
                        <select
                          value={reminderTimeValue}
                          onChange={(e) => {
                            const newValue = parseInt(e.target.value)
                            setReminderTimeValue(newValue)
                            handleSaveReminderTime(newValue, reminderTimeUnit)
                          }}
                          className="h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-0"
                          disabled={updatingReminder}
                        >
                          {Array.from({ length: 24 }, (_, i) => i + 1).map((num) => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                        <select
                          value={reminderTimeUnit}
                          onChange={(e) => {
                            const newUnit = e.target.value as 'hours' | 'days'
                            setReminderTimeUnit(newUnit)
                            handleSaveReminderTime(reminderTimeValue, newUnit)
                          }}
                          className="h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-0"
                          disabled={updatingReminder}
                        >
                          <option value="hours">hours</option>
                          <option value="days">days</option>
                        </select>
                        <span className="text-sm text-muted-foreground">before booking</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Clients will receive a reminder {reminderTimeValue} {reminderTimeUnit === 'hours' ? (reminderTimeValue === 1 ? 'hour' : 'hours') : (reminderTimeValue === 1 ? 'day' : 'days')} before their scheduled appointment.
                      </p>
                    </div>

                    {/* Custom Message Settings */}
                    <div className="space-y-3 pt-3 border-t border-input">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">Custom Message</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {customMessageEnabled ? 'Custom message is enabled' : 'Custom message is disabled'}
                          </p>
                        </div>
                        <button
                          onClick={handleToggleCustomMessage}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                            customMessageEnabled ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          aria-pressed={customMessageEnabled}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                              customMessageEnabled ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {customMessageEnabled && (
                        <div className="space-y-3">
                          <p className="text-sm text-muted-foreground">
                            Customize the message clients receive in their notification.
                          </p>

                          <textarea
                            value={editedReminderMessage}
                            onChange={(e) => setEditedReminderMessage(e.target.value)}
                            placeholder="Enter your reminder message..."
                            className="w-full min-h-[100px] p-3 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-0"
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={handleSaveReminderMessage}
                              disabled={isSavingReminderMessage}
                              className="flex-1"
                            >
                              {isSavingReminderMessage ? (
                                <>
                                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                'Save'
                              )}
                            </Button>
                            <Button
                              onClick={handleCancelReminderMessageEdit}
                              disabled={isSavingReminderMessage}
                              variant="outline"
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Client Booking Info Section */}
          <div className="bg-card rounded-lg overflow-hidden" ref={clientBookingInfoRef}>
            {/* Dropdown Header */}
            <button
              onClick={() => {
                setIsClientBookingInfoExpanded(!isClientBookingInfoExpanded)
                if (!isClientBookingInfoExpanded) scrollToSection(clientBookingInfoRef)
              }}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <ClipboardList className="w-5 h-5 flex-shrink-0" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold">Client Booking Info</h2>
                  <p className="text-xs text-muted-foreground">Customize booking form questions</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ${
                  isClientBookingInfoExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown Content */}
            <div
              className={`overflow-hidden transition-all duration-200 ${
                isClientBookingInfoExpanded ? 'max-h-[1500px]' : 'max-h-0'
              }`}
            >
              <div className="border-t border-input p-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Customize the questions you want to ask clients when they make a booking. <br /> We have already collected the clients email, IG handle and phone number on sign up.
                </p>

                {/* Preview - How it looks to the user */}
                {requiredInformation && (Array.isArray(requiredInformation) ? requiredInformation.length > 0 : Object.keys(requiredInformation).length > 0) && (
                  <div className="bg-background rounded-lg p-4 border border-input">
                    <p className="text-xs font-medium text-muted-foreground mb-3">Preview for users:</p>
                    <div className="space-y-3">
                      {Array.isArray(requiredInformation) ? (
                        // Array format (bookingClientInfo)
                        requiredInformation.map((fieldConfig: any, index: number) => {
                          const isDragged = draggedIndex === index
                          const isDragOver = draggedIndex !== null && draggedIndex !== index
                          
                          return (
                          <motion.div
                            key={index}
                            draggable
                            onDragStart={() => setDraggedIndex(index)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault()
                              if (draggedIndex !== null && draggedIndex !== index) {
                                handleReorderQuestions(draggedIndex, index)
                              }
                            }}
                            onDragEnd={() => setDraggedIndex(null)}
                            layout
                            animate={{
                              opacity: isDragged ? 0.5 : 1,
                              scale: isDragged ? 0.95 : 1,
                              y: 0
                            }}
                            transition={{
                              type: "spring",
                              stiffness: 300,
                              damping: 30,
                              opacity: { duration: 0.2 }
                            }}
                            className={`p-3 border border-input rounded-lg transition-all ${
                              isDragged 
                                ? 'opacity-50 bg-muted shadow-lg' 
                                : isDragOver
                                ? 'hover:bg-muted/50 shadow-md'
                                : 'hover:bg-muted/50'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <button
                                onMouseDown={(e) => e.preventDefault()}
                                className="p-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                                title="Drag to reorder"
                              >
                                <GripVertical className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteQuestion(index)}
                                disabled={isSavingQuestion}
                                className="p-1 hover:bg-red-50 rounded transition-colors text-muted-foreground hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                title="Delete field"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                            {fieldConfig.type === 'input' && (
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  {fieldConfig.displayText || fieldConfig.displayName}:
                                  {fieldConfig.isRequired && ' *'}
                                </label>
                                <input
                                  type="text"
                                  placeholder={fieldConfig.hint || ''}
                                  disabled
                                  className="w-full px-3 py-2 text-sm text-gray-500 bg-gray-50 border border-gray-300 rounded-md cursor-not-allowed"
                                />
                              </div>
                            )}
                            {fieldConfig.type === 'text' && (
                              <input
                                type="text"
                                placeholder={fieldConfig.hint || fieldConfig.displayText}
                                disabled
                                className="w-full px-3 py-2 text-sm text-gray-500 bg-gray-50 border border-gray-300 rounded-md cursor-not-allowed"
                              />
                            )}
                            {fieldConfig.type === 'number' && (
                              <input
                                type="number"
                                placeholder={fieldConfig.hint || fieldConfig.displayText}
                                disabled
                                className="w-full px-3 py-2 text-sm text-gray-500 bg-gray-50 border border-gray-300 rounded-md cursor-not-allowed"
                              />
                            )}
                            {fieldConfig.type === 'consentForm' && (
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  disabled
                                  className="mt-1 h-4 w-4 bg-gray-50 border border-gray-300 rounded cursor-not-allowed accent-primary"
                                />
                                <div className="flex-1">
                                  <label className="text-xs text-gray-600">
                                    I have checked the{' '}
                                    <button
                                      onClick={() => setViewingConsentIndex(index)}
                                      className="font-medium text-primary underline hover:text-primary/80 transition-colors cursor-pointer"
                                    >
                                      {fieldConfig.displayText || fieldConfig.displayName || 'Consent Form'}
                                    </button>
                                    {' '}and agree to it
                                  </label>
                                </div>
                              </div>
                            )}
                          </motion.div>
                          )
                        })
                      ) : (
                        // Map format (bookingRequiredInformation - legacy)
                        Object.entries(requiredInformation).map(([fieldName, fieldConfig]: any) => (
                          <div key={fieldName}>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-xs font-medium text-foreground">
                                {fieldConfig.displayText || fieldConfig.displayName || fieldName}
                                {fieldConfig.isRequired && ' *'}
                              </label>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleDeleteQuestion(fieldName)}
                                  disabled={isSavingQuestion}
                                  className="p-1 hover:bg-red-50 rounded transition-colors text-muted-foreground hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete field"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            {fieldConfig.type === 'text' && (
                              <input
                                type="text"
                                placeholder={fieldConfig.hint || fieldConfig.displayText || fieldName}
                                disabled
                                className="w-full px-3 py-2 text-sm text-gray-500 bg-gray-50 border border-gray-300 rounded-md cursor-not-allowed"
                              />
                            )}
                            {fieldConfig.type === 'number' && (
                              <input
                                type="number"
                                placeholder={fieldConfig.hint || fieldConfig.displayText || fieldName}
                                disabled
                                className="w-full px-3 py-2 text-sm text-gray-500 bg-gray-50 border border-gray-300 rounded-md cursor-not-allowed"
                              />
                            )}
                            {fieldConfig.type === 'consentForm' && (
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  disabled
                                  className="mt-1 h-4 w-4 bg-gray-50 border border-gray-300 rounded cursor-not-allowed accent-primary"
                                />
                                <div className="flex-1 flex items-center justify-between">
                                  <label className="text-xs text-gray-600">
                                    I have checked the{' '}
                                    <span className="font-medium text-foreground">
                                      {fieldConfig.displayText || fieldConfig.displayName || fieldName}
                                    </span>
                                    {' '}and agree
                                  </label>
                                  <button
                                    disabled
                                    className="ml-2 px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-md cursor-not-allowed"
                                  >
                                    View
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Add Field Form */}
                {isAddingQuestion ? (
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-input">
                    <h3 className="font-semibold text-sm">Add New Question</h3>
                    
                    {/* Field Type Selection */}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-2 block">
                        Field Type:
                      </label>
                      <select
                        value={newFieldType}
                        onChange={(e) => setNewFieldType(e.target.value as any)}
                        className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-0"
                      >
                        <option value="input">Input</option>
                        <option value="consentForm">Consent Form</option>
                      </select>
                    </div>

                    {/* Question Label - Hide for consent forms */}
                    {newFieldType !== 'consentForm' && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-2 block">
                          Question / Label:*
                        </label>
                        <input
                          type="text"
                          value={newFieldData.displayText}
                          onChange={(e) => setNewFieldData({ ...newFieldData, displayText: e.target.value })}
                          placeholder="e.g., Full Name, Budget, Address"
                          className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-0"
                        />
                      </div>
                    )}

                    {/* Hint/Placeholder - Only show for input */}
                    {newFieldType !== 'consentForm' && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-2 block">
                          Hint or Placeholder:
                        </label>
                        <input
                          type="text"
                          value={newFieldData.hint}
                          onChange={(e) => setNewFieldData({ ...newFieldData, hint: e.target.value })}
                          placeholder="e.g., John Doe, $500, My Street 123"
                          className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-0"
                        />
                      </div>
                    )}

                    {/* Consent Content */}
                    {newFieldType === 'consentForm' && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-2 block">
                          Consent Text:*
                        </label>
                        <textarea
                          value={newFieldData.content}
                          onChange={(e) => setNewFieldData({ ...newFieldData, content: e.target.value })}
                          placeholder="Enter the consent form text that users must agree to..."
                          className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-0 min-h-[100px]"
                        />
                      </div>
                    )}

                    {/* Required Checkbox */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="isRequired"
                        checked={newFieldData.isRequired}
                        onChange={(e) => setNewFieldData({ ...newFieldData, isRequired: e.target.checked })}
                        className="h-4 w-4 accent-primary"
                      />
                      <label htmlFor="isRequired" className="text-xs font-medium text-muted-foreground">
                        Required field
                      </label>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setIsAddingQuestion(false)}
                        disabled={isSavingQuestion}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAddQuestion}
                        disabled={
                          isSavingQuestion || 
                          (newFieldType === 'input' && !newFieldData.displayText.trim()) || 
                          (newFieldType === 'consentForm' && !newFieldData.content.trim())
                        }
                        className="flex-1"
                      >
                        {isSavingQuestion ? (
                          <>
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Add Question'
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={() => setIsAddingQuestion(true)}
                    variant="outline"
                    className="w-full gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Question
                  </Button>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Consent Form Preview Modal */}
      {viewingConsentIndex !== null && Array.isArray(requiredInformation) && requiredInformation[viewingConsentIndex]?.type === 'consentForm' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setViewingConsentIndex(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-lg max-w-md max-h-[80vh] overflow-y-auto shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 flex items-center justify-between p-4 border-b border-input bg-white">
              <h3 className="text-lg font-semibold text-foreground">
                {requiredInformation[viewingConsentIndex]?.displayText || requiredInformation[viewingConsentIndex]?.displayName || 'Consent Form'}
              </h3>
              <button
                onClick={() => setViewingConsentIndex(null)}
                className="p-1 hover:bg-muted rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4">
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                {requiredInformation[viewingConsentIndex]?.content}
              </p>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 p-4 border-t border-input bg-white">
              <Button
                onClick={() => setViewingConsentIndex(null)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}

